import EzController from 'controllers/ez-base-controller';
import Cookies from 'js-cookie';
import { ajaxGet, getAjaxModal, insertAjaxContent } from 'lib/ajax-helpers';
import { ezConfirm } from 'lib/modal_helpers';
import {
  getContentType,
  triggerCustomEvent,
  primeForPostLogin,
  featureEnabled,
} from 'lib/helpers';
import {
  downloadResource,
  getCustomerFeedbackModal,
  showReturningSigninModal,
} from 'lib/download-helpers';
import events from 'lib/events';
import { addSessionDownloadActivity } from 'lib/sessions-manager';
import {
  sendTrackingEvent,
  findCategoryByElement,
  trackCompleteEvent,
} from 'components/analytics/conversions/tracking';
import {
  LICENSING_TRIGGERED,
  LICENSING_COMPLETE,
  DOWNLOAD_CLICK,
  DOWNLOAD_COMPLETE,
  JOIN_TRIGGERED,
  JOIN_PRO_TRIGGERED,
  JOIN_PRO_COMPLETE,
  LOGIN_TRIGGERED,
} from 'components/analytics/conversions/constants/actions';
import axios from 'axios';
import { ezLoadingBtnOn, ezLoadingBtnOff } from 'ez-styles/assets/js/utils/buttons';
import { getResourceType } from 'lib/resource-show-helpers';
import EzUser from 'init/init-user';

export default class DownloadButtonController extends EzController {
  static values = {
    dailyDownloadCount: Number,
    dailyDownloadLimit: Number,
    inFreeTrial: Boolean,
  };

  // Let's avoid granny double clicks by only re-enabling the button after JS is done.
  clickProcessing = false;

  connect() {
    this.mainDlBtn = this.element;
    if (!this.isTurbolinksPreview) {
      const searchData = window.localStorage.getItem(`resource-${this.resourceId}`);
      if (searchData) {
        this.searchData = JSON.parse(searchData);
        window.localStorage.removeItem(`resource-${this.resourceId}`);
      }
      this.flagLoaded();

      this.checkDownloadCookie();
    }

    if (this.element.id === 'pro-btn') {
      this.trackLicenseTriggerEvent();
    }
  }

  disconnect() {
    window.removeEventListener('ProceedWithFreeDownload', this.proceedWithDownload);
  }

  previewImageTriggerClick = () => {
    this.sendDownloadEvent();

    if (this.resourceLicense === 'editorial') {
      this.freeDownload();
    } else {
      getAjaxModal(this.element.href);
    }
  }

  get resourceLicense() {
    return document.querySelector('.ez-resource-show').dataset.resourceType;
  }

  proJoin = (event) => {
    Cookies.set('download_size_for_checkout', event.currentTarget.dataset.size);
  }

  proDownload = (event = null) => {
    if (event) event.preventDefault();
    if (this.clickProcessing === true) return;
    this.clickProcessing = true;

    const downloadSize = this.getDownloadSize(event, event?.currentTarget);

    if (this.exceededDownloadLimit) {
      this.openDownloadLimitModal();
    } else {
      this.triggerDownload(event, { size: downloadSize });
      this.dailyDownloadCountValue += 1;
    }
    this.clickProcessing = false;

    if (!event?.currentTarget.hasAttribute('data-license-selector-credit-radio-target')) {
      getCustomerFeedbackModal();
    }
  }

  freeDownload = (event = null) => {
    if (event) event.preventDefault();
    if (this.clickProcessing === true) return;
    this.clickProcessing = true;

    const downloadSize = this.getDownloadSize(event, event?.currentTarget);

    if (showReturningSigninModal()) {
      if (this.element.dataset.dlAdModalPath) {
        this.setDownloadCookie(this.element.dataset.dlAdModalPath, downloadSize);
      }

      this.dailyDownloadCountValue -= 1;
      this.openReturningSigninModal();
    } else if (this.exceededDownloadLimit) {
      if (this.element.dataset.dlAdModalPath) {
        this.setDownloadCookie(this.element.dataset.dlAdModalPath, downloadSize);
      }

      this.openDownloadLimitModal();
    } else if (this.element.dataset.dlAdModalPath) {
      this.showDownloadAdPopup();

      if (downloadSize !== 'original') {
        const downloadFileUrl = this.getDownloadFileUrl(event);

        this.processDlSize({ url: downloadFileUrl, size: downloadSize });
      }

      if (this.element.getAttribute('data-license-type') !== 'editorial') {
        this.showFreeAttributionSlidedown();
      }
    } else {
      this.triggerDownload(event, { size: downloadSize });
    }

    if (document.querySelector('.is-logged-in') && featureEnabled('customer_feedback')) {
      getCustomerFeedbackModal();
    }

    this.clickProcessing = false;
    this.dailyDownloadCountValue += 1;
  }

  getDownloadSize(event, buttonTarget = null) {
    const defaultSize = 'original';

    if (!event) return defaultSize;

    if (typeof event.detail.dlSize !== 'undefined') return event.detail.dlSize;

    if (buttonTarget && buttonTarget.dataset && (typeof buttonTarget.dataset.size !== 'undefined')) {
      return buttonTarget.dataset.size;
    }

    return defaultSize;
  }

  getDownloadFileUrl(event = null) {
    const defaultUrl = this.element.getAttribute('href');

    if (!event) return defaultUrl;

    if (typeof event.detail.downloadFileUrl !== 'undefined') return event.detail.downloadFileUrl;

    return defaultUrl;
  }

  showFreeAttributionSlidedown() {
    // first, get rid of any existing slidedowns
    if (document.querySelector('[data-controller="free-attribution-slidedown"]')) {
      const existingSlidedown = document.querySelector('[data-controller="free-attribution-slidedown"]');

      existingSlidedown.remove();
    }

    const resourceId = document.querySelector('[data-controller*="resource-show-main"]').getAttribute('data-resource-id');

    let slidedownParent;

    // start split-test: iscroll_modal
    if (document.querySelector('[data-controller="resource-show-modal-view"]')) {
      slidedownParent = document.querySelector('[data-controller="resource-show-modal-view"]');
    } else {
      slidedownParent = document.querySelector('.ez-page-wrapper__content');
    }
    // end split-test: iscroll_modal

    insertAjaxContent({
      url: `/resources/${resourceId}/show_free_attr_slidedown`,
      destElem: slidedownParent,
      destPos: 'afterbegin',
      successCb: () => {
        triggerCustomEvent(events.show_page.reveal_free_attribution_slidedown);
      },
    });
  }

  triggerDownload = (event, { size = 'original', reloadPage = false } = {}) => {
    addSessionDownloadActivity(this.resourceId);

    const downloadSize = event?.detail?.size ? event.detail.size : size;
    const downloadFileUrl = this.getDownloadFileUrl(event);

    if (downloadSize === 'original' || this.dlPopupReady) {
      if (!this.mainDlBtn.classList.contains('has-spinner')) {
        ezLoadingBtnOn(this.mainDlBtn);
      }

      this.deliverFile({ url: downloadFileUrl, downloadSize, reloadPage });
    } else {
      this.processDlSize({ url: downloadFileUrl, size: downloadSize, reloadPage });
    }
  }

  processDlSize({ url, size, reloadPage = false }) {
    if (!this.mainDlBtn.classList.contains('has-spinner')) {
      ezLoadingBtnOn(this.mainDlBtn);
    }

    ajaxGet(url,
      async (response) => {
        const s3Response = await this.pollForConvertedDlSize({
          fn: this.checkSizeConversion,
          fnCondition: this.urlCheck,
          jobId: response.data.job_id,
          interval: 1000,
        });

        this.setS3ResponseUrlOrDeliver({ url: s3Response.data.url, size, reloadPage });
      },
      (error) => {
        console.error(error); // eslint-disable-line no-console
      },
      {
        params: { size },
      });
  }

  setS3ResponseUrlOrDeliver({ url, size, reloadPage = false }) {
    const dlAdModal = document.body.querySelector('#download-popup-modal .download-popup');
    if (dlAdModal) {
      this.dlPopupReady = true;

      dlAdModal.setAttribute('data-download-file-url', url);
    } else {
      this.deliverFile({ url, downloadSize: size, reloadPage });
    }
  }

  urlCheck = (result) => typeof result.data.url === 'undefined'

  pollForConvertedDlSize = async ({
    fn, fnCondition, jobId, interval,
  }) => {
    let result = await fn(jobId);

    /* eslint-disable no-await-in-loop */
    while (fnCondition(result)) {
      await this.waitForConvertedDlSize(interval);
      result = await fn(jobId);
    }
    /* eslint-enable no-await-in-loop */

    return result;
  }

  waitForConvertedDlSize = (interval = 1000) => new Promise((resolve) => {
    setTimeout(resolve, interval);
  })

  checkSizeConversion = (jobId) => axios.get(`/api/v2/resource_conversion/${jobId}`);

  deliverFile({ url, downloadSize = 'original', reloadPage = false }) {
    sendTrackingEvent({
      data: {
        action: DOWNLOAD_COMPLETE,
        label: getResourceType(),
        category: 'Show',
        content_type: `Content-${this.resourceContentType}`,
      },
    });

    downloadResource({ url, downloadSize, reloadPage });

    if (this.mainDlBtn.classList.contains('has-spinner')) {
      const dlBtn = this.mainDlBtn;

      if (downloadSize === 'original') {
        setTimeout(() => {
          ezLoadingBtnOff(dlBtn);
          if (document.querySelector('.ez-drop-down--download-btn')) {
            document.querySelector('.ez-drop-down--download-btn').classList.add('is-closed');
          }
        }, 1000);
      } else {
        ezLoadingBtnOff(dlBtn);
      }
    }
  }

  showDownloadAdPopup() {
    const downloadAdModalPath = this.element.dataset.dlAdModalPath;

    this.element.setAttribute('remote', 'true');

    window.addEventListener('TriggerDownload', this.triggerDownload, { once: true });

    getAjaxModal(downloadAdModalPath);
  }

  setDownloadCookie(dlPopupLink, downloadSize) {
    primeForPostLogin('download_after_social_signin', dlPopupLink);
    Cookies.set('download_size', downloadSize);
  }

  checkDownloadCookie() {
    const dlPopupLink = Cookies.get('download_after_social_signin');
    const downloadSize = Cookies.get('download_size');

    if (!this.exceededDownloadLimit && dlPopupLink && EzUser.signedIn) {
      const urlParams = new URLSearchParams(window.location.search);

      if (!urlParams.has('autodl_token')) {
        this.element.setAttribute('remote', 'true');
        window.addEventListener('TriggerDownload', this.triggerDownload, { once: true });

        if (downloadSize && downloadSize !== 'original') {
          const downloadFileUrl = document.querySelector('.ez-resource-show__sidebar__download-options').dataset.downloadUrl;

          this.processDlSize({ url: downloadFileUrl, size: downloadSize });
        }

        getAjaxModal(dlPopupLink);
      }
    }

    Cookies.remove('download_after_social_signin');
    Cookies.remove('download_size');
  }

  openDownloadLimitModal() {
    const { dlLimitModalPath } = this.element.dataset;

    this.element.setAttribute('remote', 'true');

    if (!this.inFreeTrialValue) {
      sendTrackingEvent({
        element: this.element,
        data: {
          action: JOIN_TRIGGERED,
          category: `Trigger-Download_Limit-${this.dailyDownloadCountValue}`,
        },
      });
    }

    getAjaxModal(dlLimitModalPath);
  }

  openReturningSigninModal() {
    const {
      returningSigninModalPath,
      licenseType,
      dlAdModalPath,
      dlLimitModalPath,
    } = this.element.dataset;

    this.element.setAttribute('remote', 'true');

    sendTrackingEvent({
      element: this.element,
      data: {
        action: LOGIN_TRIGGERED,
        category: 'Trigger-Download_Click',
      },
    });

    window.addEventListener('TriggerFreeDownload', this.freeDownload, { once: true });

    getAjaxModal(returningSigninModalPath, null, null, null,
      { // Need to pass this stuff through for the "download anyway" link
        resource_dl_path: this.element.href,
        dl_size: Cookies.get('download_size'),
        dl_license_type: licenseType,
        dl_ad_modal_path: dlAdModalPath,
        dl_limit_modal_path: dlLimitModalPath,
      });
  }

  showAccountDunningFreeModal() {
    window.addEventListener('ProceedWithFreeDownload', this.proceedWithDownload);
  }

  proceedWithDownload = () => {
    this.sendDownloadEvent();
    this.trackFreeLicenseSelectorTrigger({ currentTarget: this.element });
  }

  triggerFreeDunningDownload() {
    triggerCustomEvent('ProceedWithFreeDownload');
  }

  spendCreditAndDownload = ({ event, downloadSize }) => {
    // Need to reload the page so a new download button gets rendered
    getCustomerFeedbackModal({ onReload: true });

    this.triggerDownload(event, { size: downloadSize, reloadPage: true });

    this.creditSpent = true;
  }

  confirmCreditSpend(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const downloadSize = event.currentTarget.dataset.size || 'original';

    if (this.creditSpent) {
      this.triggerDownload(event, { size: downloadSize });
    } else {
      ezConfirm(
        event.currentTarget.getAttribute('data-confirm-text'),
        this.spendCreditAndDownload,
        ['Okay', 'Cancel'],
        { callbackArgs: { event, downloadSize } },
      );
    }
  }

  bundleCreditSpend(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const downloadSize = 'original';

    this.spendCreditAndDownload({ event, downloadSize });
  }

  get isTurbolinksPreview() {
    return document.documentElement.hasAttribute('data-turbolinks-preview');
  }

  get resourceId() {
    return document.querySelector('.ez-resource-show').dataset.resourceId;
  }

  get resourceType() {
    return document.querySelector('.ez-resource-show').dataset.resourceType;
  }

  get resourceContentType() {
    return document.body.dataset.conversionsContentType || getContentType();
  }

  get exceededDownloadLimit() {
    return this.dailyDownloadLimitValue && (this.dailyDownloadCountValue >= this.dailyDownloadLimitValue);
  }

  joinProClick(event) {
    this.sendDownloadEvent();

    const category = findCategoryByElement(event.target);

    sendTrackingEvent({
      element: event.target,
      data: {
        action: JOIN_PRO_TRIGGERED,
        category,
        label: 'Pro',
      },
    });

    trackCompleteEvent({
      action: JOIN_PRO_COMPLETE,
      category,
    });
  }

  sendDownloadEvent() {
    let eventDetails = {
      resourceId: this.resourceId,
      resourceType: this.resourceType,
    };

    if (this.searchData) {
      eventDetails = Object.assign(eventDetails, this.searchData);
    }

    triggerCustomEvent(events.download_btn.initial_click, eventDetails);

    sendTrackingEvent({
      element: this.element,
      data: {
        action: DOWNLOAD_CLICK,
        label: getResourceType(),
        category: 'Show',
        content_type: `Content-${this.resourceContentType}`,
      },
    });
  }

  trackFreeLicenseSelectorTrigger(event) {
    sendTrackingEvent({
      element: event.currentTarget,
      data: {
        action: LICENSING_TRIGGERED,
        category: 'Trigger-Download_Click',
        label: getResourceType(),
        content_type: `Content-${this.resourceContentType}`,
      },
    });
  }

  trackLicenseTriggerEvent(event) {
    sendTrackingEvent({
      element: this.element,
      data: {
        category: 'Trigger-License_Toggle',
        action: LICENSING_TRIGGERED,
        label: 'Free',
        content_type: `${getContentType(event)}`,
      },
    });
  }

  trackLicenseCompleteEvent(event) {
    sendTrackingEvent({
      element: this.element,
      data: {
        category: 'Trigger-License_Toggle',
        action: LICENSING_COMPLETE,
        label: `License-${this.element.dataset.licenseType}`,
        content_type: `${getContentType(event)}`,
      },
    });
  }

  sendDownloadEventAndRedirect(event) {
    event.preventDefault();
    this.sendDownloadEvent();

    Turbolinks.visit(this.element.href);
  }
}
